import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserPresentations, formatPresentationDate } from '../services/user-presentations-service';
import presentationUsageTracker from '../services/presentation-usage-tracker';
import { 
  Box, 
  Typography, 
  Button, 
  Card, 
  CardContent, 
  CardActions,
  Grid,
  Divider,
  Alert,
  Paper
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import LoadingPlaceholder from './LoadingPlaceholder';
import PresentationLimitBanner from './PresentationLimitBanner';

export default function Presentations() {
  const { user } = useAuth();
  const [presentations, setPresentations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [presentationUsage, setPresentationUsage] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPresentations = async () => {
      try {
        setLoading(true);
        const presentationsData = await getUserPresentations();
        setPresentations(presentationsData);
        
        // Get presentation usage count for the last 30 days
        const monthlyCount = await presentationUsageTracker.getMonthlyPresentationCount();
        setPresentationUsage(monthlyCount);
        
        setError(null);
      } catch (err) {
        console.error('Error fetching presentations:', err);
        setError('Failed to load presentations. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    if (user) {
      fetchPresentations();
    }
  }, [user]);

  const handleCreatePresentation = () => {
    navigate('/bookmarks');
  };

  const handleDownload = (downloadUrl) => {
    if (!downloadUrl) {
      setError('Download URL not available for this presentation.');
      return;
    }
    
    // Open the download URL in a new tab
    window.open(downloadUrl, '_blank');
  };

  if (loading) {
    return <LoadingPlaceholder message="Hang in tight — We are loading your presentations..." />;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Your Presentations
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          startIcon={<AddIcon />}
          onClick={handleCreatePresentation}
        >
          Create New Presentation
        </Button>
      </Box>
      
      {/* Display the presentation limit banner */}
      <PresentationLimitBanner currentUsage={presentationUsage} />

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {presentations.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            No presentations found
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You haven't created any presentations yet.
          </Typography>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            onClick={handleCreatePresentation}
          >
            Create Your First Presentation
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {presentations.map((presentation) => (
            <Grid item xs={12} sm={6} md={4} key={presentation.id}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" component="h2" gutterBottom noWrap>
                    {presentation.name || 'Presentation'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Created: {formatPresentationDate(presentation.timestamp)}
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="body2">
                    Bookmarks: {presentation.bookmarkCount || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Status: {presentation.status || 'Completed'}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => handleDownload(presentation.downloadUrl)}
                    disabled={!presentation.downloadUrl}
                  >
                    Download
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}
